import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  CalendarDays,
  Search,
  ChevronLeft,
  ChevronRight,
  LogIn,
  LogOut,
  Eye,
  User,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  RefreshCw,
  Banknote,
  Printer,
  ShieldCheck,
  ShieldX,
} from 'lucide-react';
import { getAdminBookings, getAdminBookingDetail, checkIn, checkOut, confirmPayment, declinePayment, getTodayCounts } from '../services/adminService';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Hold', value: '1' },
  { label: 'Confirmed', value: '2' },
  { label: 'Cancelled', value: '3' },
  { label: 'Checked In', value: '4' },
  { label: 'Checked Out', value: '5' },
  { label: 'No Show', value: '6' },
];

const STATUS_STYLES = {
  Hold: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
  Confirmed: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
  Cancelled: 'bg-red-500/20 text-red-400 border border-red-500/30',
  CheckedIn: 'bg-green-500/20 text-green-400 border border-green-500/30',
  CheckedOut: 'bg-slate-500/20 text-slate-400 border border-slate-500/30',
  NoShow: 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
};

const PAY_STYLES = {
  Pending: 'bg-yellow-500/20 text-yellow-400',
  Partial: 'bg-blue-500/20 text-blue-400',
  Paid: 'bg-green-500/20 text-green-400',
  Failed: 'bg-red-500/20 text-red-400',
  Refunded: 'bg-slate-500/20 text-slate-400',
};

const fmt = (d) =>
  d ? new Date(d.endsWith('Z') ? d : d + 'Z').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtMoney = (n) => `৳${Number(n).toLocaleString('en-BD', { minimumFractionDigits: 0 })}`;

const sourceLabel = (src) => {
  if (!src) return { label: 'Online', cls: 'bg-purple-500/20 text-purple-400 border-purple-500/30' };
  if (src === 'WalkIn') return { label: 'Admin', cls: 'bg-teal-500/20 text-teal-400 border-teal-500/30' };
  if (src === 'OTA') return { label: 'OTA', cls: 'bg-orange-500/20 text-orange-400 border-orange-500/30' };
  return { label: src, cls: 'bg-slate-500/20 text-slate-400 border-slate-500/30' };
};

const StatusBadge = ({ status, styles }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || 'bg-slate-600/30 text-slate-400'}`}>
    {status}
  </span>
);

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between py-1.5 border-b border-slate-700/50 last:border-0">
    <span className="text-slate-400 text-sm">{label}</span>
    <span className="text-white text-sm font-medium text-right max-w-[60%]">{value ?? '—'}</span>
  </div>
);

const toDate = (d) => new Date(d?.endsWith?.('Z') ? d : (d + 'Z'));
const isToday = (d) => toDate(d) <= new Date();

export default function Bookings() {
  const { user } = useAuth();
  const location = useLocation();

  if (user?.role === 'HotelStaff' && !user?.canMakeReservation)
    return <Navigate to="/dashboard" replace />;

  const [bookings, setBookings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState(''); // '' | 'Online' | 'WalkIn'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState(() => location.state?.search ?? '');
  const [searchInput, setSearchInput] = useState(() => location.state?.search ?? '');
  const [quickFilter, setQuickFilter] = useState(''); // 'today-checkin' | 'today-checkout' | ''
  const [todayCounts, setTodayCounts] = useState({ checkInPending: 0, checkOutPending: 0 });

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Verify Payment dialog — for guest-submitted advance (from row or detail)
  // verifyTarget: { bookingId, pnr, grandTotal, method, txnId, submittedAmount } | null
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [verifyAmount, setVerifyAmount] = useState('');
  const [verifyChecked, setVerifyChecked] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  // Manual Confirm Payment dialog — for walk-ins / no guest submission (from detail only)
  const [declineTarget, setDeclineTarget] = useState(null); // { bookingId, pnr }
  const [declineReason, setDeclineReason] = useState('');
  const [declineLoading, setDeclineLoading] = useState(false);

  const [manualPayOpen, setManualPayOpen] = useState(false);
  const [manualPayAmt, setManualPayAmt] = useState('');
  const [manualDiscType, setManualDiscType] = useState(0);   // 0=Percent, 1=Flat
  const [manualDiscValue, setManualDiscValue] = useState('');
  const [manualPayLoading, setManualPayLoading] = useState(false);

  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleSearch = () => { setSearch(searchInput.trim()); };

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, pageSize: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (search) params.search = search;
      if (quickFilter === 'today-checkin') params.todayCheckIn = true;
      if (quickFilter === 'today-checkout') params.todayCheckOut = true;
      const data = await getAdminBookings(params);
      setBookings(data.items || []);
      setTotalCount(data.totalCount || 0);
      setPage(p);
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, dateFrom, dateTo, search, quickFilter]);

  useEffect(() => { load(1); }, [load]);

  const refreshCounts = useCallback(async () => {
    try { setTodayCounts(await getTodayCounts()); } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const d = await getAdminBookingDetail(id);
      setDetail(d);
    } catch {
      toast.error('Failed to load booking detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      await checkIn(selectedId);
      toast.success('Guest checked in successfully');
      const d = await getAdminBookingDetail(selectedId);
      setDetail(d);
      load(page);
      refreshCounts();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      await checkOut(selectedId);
      toast.success('Guest checked out successfully');
      const d = await getAdminBookingDetail(selectedId);
      setDetail(d);
      load(page);
      refreshCounts();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const printVoucher = () => {
    const d = detail;
    if (!d) return;

    const fmtDate = (dt) => {
      const date = new Date(dt?.endsWith?.('Z') ? dt : dt + 'Z');
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    };
    const money = (n) => `৳${Number(n).toLocaleString('en-BD', { minimumFractionDigits: 2 })}`;
    const balance = d.grandTotal - d.paidAmount;
    const isFullyPaid = d.paymentStatus === 'Paid';

    const pricePerNight = (d.numberOfRooms > 0 && d.nights > 0)
      ? d.subTotal / d.numberOfRooms / d.nights
      : 0;

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Booking Voucher — ${d.pnr}</title>
<style>
  @page { size: A4 portrait; margin: 10mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 9pt; color: #1a1a2e; background: #fff; }
  .page { padding: 0; }

  .header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 8px; border-bottom: 2px solid #1e3a8a; margin-bottom: 8px; }
  .hotel-brand { display: flex; align-items: center; gap: 10px; }
  .hotel-logo { width: 44px; height: 44px; object-fit: contain; }
  .hotel-name { font-size: 13pt; font-weight: 700; color: #1e3a8a; }
  .hotel-contact { font-size: 7.5pt; color: #555; margin-top: 1px; }
  .voucher-title { font-size: 15pt; font-weight: 800; color: #1e3a8a; letter-spacing: 1px; text-transform: uppercase; }
  .pnr-box { display: inline-block; margin-top: 4px; background: #1e3a8a; color: #fff; padding: 3px 10px; border-radius: 3px; font-size: 8.5pt; font-weight: 700; letter-spacing: 2px; }

  .ribbon { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 5px 14px; border-radius: 5px; margin-bottom: 8px; font-weight: 700; font-size: 8.5pt; }
  .ribbon.paid { background: #dcfce7; color: #15803d; border: 1px solid #86efac; }
  .ribbon.partial { background: #fef9c3; color: #854d0e; border: 1px solid #fde047; }
  .ribbon-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .ribbon.paid .ribbon-dot { background: #16a34a; }
  .ribbon.partial .ribbon-dot { background: #ca8a04; }

  .dates-row { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 10px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 5px; padding: 7px 14px; margin-bottom: 8px; text-align: center; }
  .date-label { font-size: 7.5pt; text-transform: uppercase; letter-spacing: 1px; color: #3b82f6; font-weight: 700; margin-bottom: 2px; }
  .date-value { font-size: 11pt; font-weight: 800; color: #1e3a8a; }
  .nights-badge { background: #1e3a8a; color: #fff; padding: 4px 10px; border-radius: 14px; font-size: 8.5pt; font-weight: 700; white-space: nowrap; }

  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px; }
  .section { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 5px; padding: 7px 9px; }
  .section-title { font-size: 7pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; margin-bottom: 5px; }
  .section-row { display: flex; justify-content: space-between; padding: 2px 0; border-bottom: 1px solid #f1f5f9; font-size: 8pt; }
  .section-row:last-child { border-bottom: none; }
  .section-row .lbl { color: #64748b; }
  .section-row .val { font-weight: 600; color: #0f172a; text-align: right; max-width: 55%; }

  .price-table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  .price-table th { background: #1e3a8a; color: #fff; padding: 5px 8px; font-size: 8pt; text-align: left; }
  .price-table td { padding: 4px 8px; font-size: 8pt; border-bottom: 1px solid #f1f5f9; }
  .price-table tr:nth-child(even) td { background: #f8fafc; }
  .price-table .total-row td { background: #1e3a8a; color: #fff; font-weight: 700; border-bottom: none; }
  .price-table .paid-row td { background: #dcfce7; color: #15803d; font-weight: 700; }
  .price-table .due-row td { background: #fef9c3; color: #854d0e; font-weight: 700; }

  .footer-terms { font-size: 7.5pt; color: #94a3b8; margin-top: 8px; border-top: 1px dashed #e2e8f0; padding-top: 6px; }
  .footer-terms p { margin-bottom: 2px; }

  .sig-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 18px; }
  .sig-box { text-align: center; }
  .sig-line { border-top: 1px solid #334155; margin: 28px 10px 4px; }
  .sig-label { font-size: 8pt; color: #374151; font-weight: 600; }
  .sig-date { border-top: 1px solid #94a3b8; margin: 8px 30px 0; }
  .sig-date-label { font-size: 7pt; color: #94a3b8; margin-top: 2px; }

  .staff-line { text-align: center; margin-top: 6px; font-size: 7.5pt; color: #94a3b8; }
  .no-print { display: block; text-align: right; margin-bottom: 10px; }
  @media print { .no-print { display: none !important; } }
</style>
</head>
<body>
<div class="page">

  <div class="no-print">
    <button onclick="window.print()" style="background:#1e3a8a;color:#fff;border:none;padding:8px 20px;border-radius:5px;font-size:12px;font-weight:700;cursor:pointer;">🖨️ Print Voucher</button>
  </div>

  <div class="header">
    <div class="hotel-brand">
      ${d.hotelLogo ? `<img src="${d.hotelLogo}" class="hotel-logo" alt="Logo"/>` : `<div style="width:40px;height:40px;background:#1e3a8a;border-radius:6px;display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;font-weight:800;">${(d.hotelName||'H')[0]}</div>`}
      <div>
        <div class="hotel-name">${d.hotelName || 'Hotel'}</div>
        <div class="hotel-contact">${[d.hotelAddress, d.hotelCity].filter(Boolean).join(', ')}</div>
        <div class="hotel-contact">${[d.hotelPhone, d.hotelEmail].filter(Boolean).join(' · ')}</div>
      </div>
    </div>
    <div style="text-align:right">
      <div class="voucher-title">Booking Voucher</div>
      <div class="pnr-box">${d.pnr}</div>
    </div>
  </div>

  <div class="ribbon ${isFullyPaid ? 'paid' : 'partial'}">
    <span class="ribbon-dot"></span>
    ${isFullyPaid
      ? `FULLY PAID — ${money(d.paidAmount)}`
      : `ADVANCE PAID — ${money(d.paidAmount)} &nbsp;|&nbsp; Balance Due: ${money(balance)}`
    }
  </div>

  <div class="dates-row">
    <div><div class="date-label">Check-In</div><div class="date-value">${fmtDate(d.checkInDate)}</div></div>
    <div class="nights-badge">${d.nights} Night${d.nights !== 1 ? 's' : ''}</div>
    <div><div class="date-label">Check-Out</div><div class="date-value">${fmtDate(d.checkOutDate)}</div></div>
  </div>

  <div class="two-col">
    <div class="section">
      <div class="section-title">Guest Information</div>
      <div class="section-row"><span class="lbl">Name</span><span class="val">${d.leadPassenger ? `${d.leadPassenger.firstName} ${d.leadPassenger.lastName}` : '—'}</span></div>
      ${d.leadPassenger?.email ? `<div class="section-row"><span class="lbl">Email</span><span class="val">${d.leadPassenger.email}</span></div>` : ''}
      ${d.leadPassenger?.phone ? `<div class="section-row"><span class="lbl">Phone</span><span class="val">${d.leadPassenger.phone}</span></div>` : ''}
      ${d.leadPassenger?.city ? `<div class="section-row"><span class="lbl">City</span><span class="val">${d.leadPassenger.city}</span></div>` : ''}
      ${d.leadPassenger?.documentType ? `<div class="section-row"><span class="lbl">${d.leadPassenger.documentType}</span><span class="val">${d.leadPassenger.documentNumber || '—'}</span></div>` : ''}
    </div>
    <div class="section">
      <div class="section-title">Booking Details</div>
      <div class="section-row"><span class="lbl">Room Type</span><span class="val">${d.roomTypeName}</span></div>
      <div class="section-row"><span class="lbl">Rooms</span><span class="val">${d.numberOfRooms}</span></div>
      <div class="section-row"><span class="lbl">Guests</span><span class="val">${d.adults} Adult${d.adults !== 1 ? 's' : ''}${d.children > 0 ? `, ${d.children} Child${d.children !== 1 ? 'ren' : ''}` : ''}</span></div>
      <div class="section-row"><span class="lbl">Booked On</span><span class="val">${fmtDate(d.bookedAt)}</span></div>
      <div class="section-row"><span class="lbl">Status</span><span class="val">${d.status}</span></div>
      ${d.source ? `<div class="section-row"><span class="lbl">Source</span><span class="val">${d.source}</span></div>` : ''}
    </div>
  </div>

  <table class="price-table">
    <thead><tr><th>Description</th><th style="text-align:right">Amount</th></tr></thead>
    <tbody>
      <tr><td>Room Charge — ${d.roomTypeName}<br/><span style="font-size:7pt;color:#64748b;">${money(pricePerNight)}/night × ${d.numberOfRooms} room${d.numberOfRooms !== 1 ? 's' : ''} × ${d.nights} night${d.nights !== 1 ? 's' : ''}</span></td><td style="text-align:right">${money(d.subTotal)}</td></tr>
      <tr><td>Tax</td><td style="text-align:right">${money(d.taxAmount)}</td></tr>
      ${d.serviceChargeAmount > 0 ? `<tr><td>Service Charge</td><td style="text-align:right">${money(d.serviceChargeAmount)}</td></tr>` : ''}
      ${d.discountAmount > 0 ? (d.couponCode
        ? `<tr><td style="color:#15803d;">Coupon (${d.couponCode})</td><td style="text-align:right;color:#15803d;">− ${money(d.discountAmount)}</td></tr>`
        : `<tr><td style="color:#dc2626;">Discount</td><td style="text-align:right;color:#dc2626;">− ${money(d.discountAmount)}</td></tr>`)
        : ''}
      <tr class="total-row"><td>Grand Total</td><td style="text-align:right">${money(d.grandTotal)}</td></tr>
      <tr class="paid-row"><td>Amount Paid</td><td style="text-align:right">${money(d.paidAmount)}</td></tr>
      ${!isFullyPaid ? `<tr class="due-row"><td>Balance Due</td><td style="text-align:right">${money(balance)}</td></tr>` : ''}
    </tbody>
  </table>

  ${(d.features?.length > 0 || d.amenities?.length > 0 || d.rules?.length > 0) ? `
  <div class="two-col">
    ${(d.features?.length > 0 || d.amenities?.length > 0) ? `
    <div class="section">
      ${d.features?.length > 0 ? `<div class="section-title">Room Features</div><div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:${d.amenities?.length > 0 ? '6px' : '0'};">${d.features.map(f => `<span style="background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe;border-radius:3px;padding:2px 7px;font-size:7pt;font-weight:600;">${f}</span>`).join('')}</div>` : ''}
      ${d.amenities?.length > 0 ? `<div class="section-title" style="margin-top:${d.features?.length > 0 ? '3px' : '0'};">Amenities</div><div style="display:grid;grid-template-columns:1fr 1fr;gap:2px;">${d.amenities.map(a => `<div style="font-size:7.5pt;color:#374151;padding:1px 0;">✓ ${a}</div>`).join('')}</div>` : ''}
    </div>` : '<div></div>'}
    ${d.rules?.length > 0 ? `
    <div class="section">
      <div class="section-title">Policies &amp; Rules</div>
      <ul style="list-style:none;">${d.rules.map(r => `<li style="font-size:7.5pt;color:#374151;padding:2px 0;border-bottom:1px solid #f1f5f9;">⚬ ${r}</li>`).join('')}</ul>
    </div>` : '<div></div>'}
  </div>` : ''}

  <div class="footer-terms">
    <p>• Please present this voucher at check-in.&nbsp;&nbsp;• Valid ID required at check-in.${!isFullyPaid ? `&nbsp;&nbsp;• Balance of ${money(balance)} payable at check-in.` : ''}&nbsp;&nbsp;• This is a computer-generated document.</p>
  </div>

  <div class="sig-row">
    <div class="sig-box">
      <div class="sig-line"></div>
      <div class="sig-label">Guest Signature</div>
      <div class="sig-date"></div>
      <div class="sig-date-label">Date</div>
    </div>
    <div class="sig-box">
      <div class="sig-line"></div>
      <div class="sig-label">Authorised Signature</div>
    </div>
  </div>

  <div class="staff-line">
    ${[
      (() => { const n = d.bookedByName || (d.source !== 'WalkIn' && d.leadPassenger ? `${d.leadPassenger.firstName} ${d.leadPassenger.lastName}`.trim() : null); return n ? `Booked by ${n}` : null; })(),
      d.confirmedByName ? `Confirmed by ${d.confirmedByName}` : null,
      d.checkedInByName  ? `Checked in by ${d.checkedInByName}`  : null,
      d.checkedOutByName ? `Checked out by ${d.checkedOutByName}` : null,
    ].filter(Boolean).join(' · ')}
  </div>
  <div class="staff-line" style="margin-top:3px;">Generated on ${new Date().toLocaleString('en-GB')} · ${d.hotelName}</div>

</div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  // Open Verify dialog — from row icon (passes list item) or from detail button
  const openVerify = (source) => {
    // source is either a list booking item (b) or the current detail object
    const isDetail = !!source.manualPaymentRef && source.bookingId !== undefined;
    setVerifyTarget({
      bookingId:       source.bookingId,
      pnr:             source.pnr,
      grandTotal:      source.grandTotal,
      method:          source.manualPaymentMethod || '',
      txnId:           source.manualPaymentRef || '',
      submittedAmount: source.manualPaymentAmount > 0 ? source.manualPaymentAmount : null,
    });
    setVerifyAmount(String(
      source.manualPaymentAmount > 0 ? source.manualPaymentAmount : source.grandTotal
    ));
    setVerifyChecked(false);
  };

  const handleVerifyConfirm = async () => {
    const amt = parseFloat(verifyAmount);
    if (isNaN(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setVerifyLoading(true);
    try {
      const updated = await confirmPayment(verifyTarget.bookingId, amt, null);
      toast.success('Payment verified and confirmed');
      setVerifyTarget(null);
      // If detail dialog for same booking is open, refresh it
      if (selectedId === verifyTarget.bookingId) setDetail(updated);
      load(page);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setVerifyLoading(false);
    }
  };

  const openDecline = (source) => {
    setDeclineReason('');
    setDeclineTarget({ bookingId: source.bookingId, pnr: source.pnr });
  };

  const handleDeclineConfirm = async () => {
    if (!declineReason.trim()) { toast.error('Please enter a reason for declining'); return; }
    setDeclineLoading(true);
    try {
      const updated = await declinePayment(declineTarget.bookingId, declineReason);
      toast.success('Payment declined. Guest can re-submit.');
      setDeclineTarget(null);
      if (selectedId === declineTarget.bookingId) setDetail(updated);
      load(page);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeclineLoading(false);
    }
  };

  // Open Manual Confirm dialog — from detail, for walk-ins (no guest submission)
  const openManualPay = () => {
    setManualPayAmt(String(detail.grandTotal));
    setManualDiscType(0);
    setManualDiscValue('');
    setManualPayOpen(true);
  };

  const handleManualPayConfirm = async () => {
    const discVal = parseFloat(manualDiscValue);
    const amt     = parseFloat(manualPayAmt);
    const hasDisc = !isNaN(discVal) && discVal > 0;
    if (!hasDisc && (isNaN(amt) || amt <= 0)) { toast.error('Enter an amount'); return; }
    setManualPayLoading(true);
    try {
      const updated = await confirmPayment(
        selectedId,
        hasDisc ? 0 : amt,
        hasDisc ? manualDiscType : null,
        hasDisc ? discVal : null
      );
      setDetail(updated);
      setManualPayOpen(false);
      toast.success('Payment confirmed');
      load(page);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setManualPayLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Bookings</h1>
          <p className="text-slate-400 text-sm mt-1">{totalCount} total bookings</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => load(page)}
          className="text-slate-400 hover:text-white"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4 space-y-3">
          {/* Search by PNR or phone */}
          <div className="flex gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search by PNR or phone number…"
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 pl-9 h-9 text-sm"
              />
            </div>
            <Button onClick={handleSearch} size="sm" className="bg-blue-600 hover:bg-blue-700 h-9 px-4">
              Search
            </Button>
            {search && (
              <Button
                variant="ghost" size="sm"
                onClick={() => { setSearch(''); setSearchInput(''); }}
                className="text-slate-400 hover:text-white h-9 px-3"
              >
                Clear
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            {/* Status filter */}
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map(s => (
                <button
                  key={s.value}
                  onClick={() => setStatusFilter(s.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    statusFilter === s.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Source filter */}
            <div className="flex gap-1.5">
              {[['', 'All'], ['Online', 'Online'], ['WalkIn', 'Walk-in']].map(([val, label]) => (
                <button key={val}
                  onClick={() => setSourceFilter(val)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    sourceFilter === val
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}>
                  {label}
                </button>
              ))}
            </div>

            {/* Today quick filters */}
            <div className="flex gap-1.5">
              <button
                onClick={() => setQuickFilter(quickFilter === 'today-checkin' ? '' : 'today-checkin')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                  quickFilter === 'today-checkin'
                    ? 'bg-green-600 text-white shadow shadow-green-900/40'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <LogIn className="h-3 w-3" /> Today Check-In
                <span className={`min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1 ${
                  quickFilter === 'today-checkin'
                    ? 'bg-white/20 text-white'
                    : todayCounts.checkInPending > 0
                      ? 'bg-green-500 text-white'
                      : 'bg-slate-600 text-slate-400'
                }`}>
                  {todayCounts.checkInPending}
                </span>
              </button>
              <button
                onClick={() => setQuickFilter(quickFilter === 'today-checkout' ? '' : 'today-checkout')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                  quickFilter === 'today-checkout'
                    ? 'bg-indigo-600 text-white shadow shadow-indigo-900/40'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <LogOut className="h-3 w-3" /> Today Check-Out
                <span className={`min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1 ${
                  quickFilter === 'today-checkout'
                    ? 'bg-white/20 text-white'
                    : todayCounts.checkOutPending > 0
                      ? 'bg-indigo-500 text-white'
                      : 'bg-slate-600 text-slate-400'
                }`}>
                  {todayCounts.checkOutPending}
                </span>
              </button>
            </div>

            {/* Date filters */}
            <div className="flex gap-2 items-center ml-auto">
              <CalendarDays className="h-4 w-4 text-slate-400" />
              <Input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white text-sm h-8 w-36"
              />
              <span className="text-slate-500 text-sm">—</span>
              <Input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white text-sm h-8 w-36"
              />
              {(dateFrom || dateTo) && (
                <Button
                  variant="ghost" size="sm"
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  className="text-slate-400 hover:text-white h-8 px-2"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-16">
              <Search className="h-10 w-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No bookings found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">PNR</th>
                    <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Guest</th>
                    <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Room Type</th>
                    <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Check-In</th>
                    <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Check-Out</th>
                    <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Amount</th>
                    <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Payment</th>
                    <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Source</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b, i) => (
                    <tr
                      key={b.bookingId}
                      className={`border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors ${
                        i % 2 === 0 ? '' : 'bg-slate-800/30'
                      }`}
                    >
                      <td className="px-4 py-3 text-sm font-mono text-blue-400">{b.pnr}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-white font-medium">{b.guestName}</div>
                        {b.guestPhone && (
                          <div className="text-xs text-slate-400">{b.guestPhone}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">{b.roomTypeName}</td>
                      <td className="px-4 py-3 text-sm text-slate-300">{fmt(b.checkInDate)}</td>
                      <td className="px-4 py-3 text-sm text-slate-300">{fmt(b.checkOutDate)}</td>
                      <td className="px-4 py-3 text-sm text-white font-medium">{fmtMoney(b.grandTotal)}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={b.status} styles={STATUS_STYLES} />
                        {b.status === 'Hold' && b.holdExpiresAt && (() => {
                          const exp = new Date(b.holdExpiresAt.endsWith?.('Z') ? b.holdExpiresAt : b.holdExpiresAt + 'Z');
                          const mins = Math.max(0, Math.ceil((exp - new Date()) / 60000));
                          return (
                            <div className="text-[10px] text-yellow-500 mt-0.5">
                              {mins > 0 ? `Expires in ${mins}m` : 'Expiring...'}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={b.paymentStatus} styles={PAY_STYLES} />
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const s = sourceLabel(b.source);
                          return (
                            <div>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium border ${s.cls}`}>{s.label}</span>
                              {b.bookedByName && <p className="text-slate-400 text-xs mt-0.5 leading-tight">{b.bookedByName}</p>}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {/* Guest submitted advance — show green Verify + red Decline icons */}
                          {b.paymentStatus === 'Pending' &&
                           b.manualPaymentRef &&
                           !['Cancelled', 'CheckedOut', 'NoShow'].includes(b.status) && (
                            <>
                              <Button
                                variant="ghost" size="icon"
                                onClick={() => openVerify(b)}
                                title="Verify Guest Payment"
                                className="h-7 w-7 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-600/20"
                              >
                                <ShieldCheck className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost" size="icon"
                                onClick={() => openDecline(b)}
                                title="Decline Payment"
                                className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-600/20"
                              >
                                <ShieldX className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          {/* No guest submission — amber manual confirm (walk-in etc.) */}
                          {b.paymentStatus === 'Pending' &&
                           !b.manualPaymentRef &&
                           !['Cancelled', 'CheckedOut', 'NoShow'].includes(b.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDetail(b.bookingId)}
                              title="Confirm Payment"
                              className="h-7 w-7 text-amber-400 hover:text-amber-300 hover:bg-amber-600/20"
                            >
                              <Banknote className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDetail(b.bookingId)}
                            className="h-8 w-8 text-slate-400 hover:text-white"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
            <span className="text-sm text-slate-400">
              Page {page} of {totalPages} ({totalCount} bookings)
            </span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                disabled={page <= 1}
                onClick={() => load(page - 1)}
                className="h-8 w-8 text-slate-400 hover:text-white disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                disabled={page >= totalPages}
                onClick={() => load(page + 1)}
                className="h-8 w-8 text-slate-400 hover:text-white disabled:opacity-30"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Booking Detail Dialog */}
      <Dialog open={selectedId !== null} onOpenChange={(open) => { if (!open) { setSelectedId(null); setDetail(null); } }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-blue-400" />
              Booking Detail
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : detail ? (
            <div className="space-y-4">
              {/* Status + Actions */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex gap-2">
                  <StatusBadge status={detail.status} styles={STATUS_STYLES} />
                  <StatusBadge status={detail.paymentStatus} styles={PAY_STYLES} />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {/* Guest submitted advance — Verify + Decline buttons */}
                  {detail.paymentStatus === 'Pending' &&
                   detail.manualPaymentRef &&
                   !['Cancelled','CheckedOut','NoShow'].includes(detail.status) && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => openVerify(detail)}
                        disabled={actionLoading}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                      >
                        <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                        Verify Payment
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openDecline(detail)}
                        disabled={actionLoading}
                        className="bg-red-700 hover:bg-red-800 text-white h-8 text-xs"
                      >
                        <ShieldX className="h-3.5 w-3.5 mr-1.5" />
                        Decline
                      </Button>
                    </>
                  )}
                  {/* No guest submission — manual confirm */}
                  {detail.paymentStatus === 'Pending' &&
                   !detail.manualPaymentRef &&
                   !['Cancelled','CheckedOut','NoShow'].includes(detail.status) && (
                    <Button
                      size="sm"
                      onClick={openManualPay}
                      disabled={actionLoading}
                      className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
                    >
                      <Banknote className="h-3.5 w-3.5 mr-1.5" />
                      Confirm Payment
                    </Button>
                  )}

                  {/* Check In — only after payment confirmed AND check-in date has arrived */}
                  {(detail.status === 'Confirmed' || detail.status === 'Hold') &&
                   detail.paymentStatus !== 'Pending' &&
                   isToday(detail.checkInDate) && (
                    <Button
                      size="sm"
                      onClick={handleCheckIn}
                      disabled={actionLoading}
                      className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs"
                    >
                      <LogIn className="h-3.5 w-3.5 mr-1.5" />
                      Check In
                    </Button>
                  )}

                  {/* Paid but check-in date hasn't arrived yet */}
                  {(detail.status === 'Confirmed' || detail.status === 'Hold') &&
                   detail.paymentStatus !== 'Pending' &&
                   !isToday(detail.checkInDate) && (
                    <span className="text-xs text-slate-400 self-center">
                      Check-in on {toDate(detail.checkInDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </span>
                  )}

                  {/* Check Out */}
                  {detail.status === 'CheckedIn' && (
                    <Button
                      size="sm"
                      onClick={handleCheckOut}
                      disabled={actionLoading}
                      className="bg-slate-600 hover:bg-slate-500 text-white h-8 text-xs"
                    >
                      <LogOut className="h-3.5 w-3.5 mr-1.5" />
                      Check Out
                    </Button>
                  )}

                  {/* Print Voucher — for paid/partial bookings */}
                  {(detail.paymentStatus === 'Paid' || detail.paymentStatus === 'Partial') && (
                    <Button
                      size="sm"
                      onClick={printVoucher}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs"
                    >
                      <Printer className="h-3.5 w-3.5 mr-1.5" />
                      Print Voucher
                    </Button>
                  )}
                </div>
              </div>

              {/* Booking Info */}
              <Card className="bg-slate-700/50 border-slate-600">
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm text-slate-300 font-medium">Booking Info</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <DetailRow label="PNR" value={<span className="font-mono text-blue-400">{detail.pnr}</span>} />
                  <DetailRow label="Room Type" value={detail.roomTypeName} />
                  <DetailRow label="Rooms" value={detail.numberOfRooms} />
                  <DetailRow label="Guests" value={`${detail.adults} Adults${detail.children > 0 ? `, ${detail.children} Children` : ''}`} />
                  <DetailRow label="Check-In" value={fmt(detail.checkInDate)} />
                  <DetailRow label="Check-Out" value={fmt(detail.checkOutDate)} />
                  <DetailRow label="Nights" value={detail.nights} />
                  <DetailRow label="Booked At" value={fmt(detail.bookedAt)} />
                  {detail.cancelledAt && <DetailRow label="Cancelled At" value={fmt(detail.cancelledAt)} />}
                  {(() => { const s = sourceLabel(detail.source); return (
                    <DetailRow label="Source" value={<span className={`px-2 py-0.5 rounded text-xs font-medium border ${s.cls}`}>{s.label}</span>} />
                  ); })()}
                </CardContent>
              </Card>

              {/* Price Breakdown */}
              <Card className="bg-slate-700/50 border-slate-600">
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm text-slate-300 font-medium flex items-center gap-1.5">
                    <CreditCard className="h-4 w-4" /> Price Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <DetailRow
                    label={`Room (${detail.numberOfRooms} × ${detail.nights} night${detail.nights !== 1 ? 's' : ''} × ${fmtMoney(detail.subTotal / (detail.numberOfRooms * detail.nights))})`}
                    value={fmtMoney(detail.subTotal)}
                  />
                  <DetailRow label="Tax" value={fmtMoney(detail.taxAmount)} />
                  {detail.serviceChargeAmount > 0 && (
                    <DetailRow label="Service Charge" value={fmtMoney(detail.serviceChargeAmount)} />
                  )}
                  {detail.couponCode && (
                    <DetailRow
                      label={<span className="text-green-400 flex items-center gap-1">🏷 Coupon ({detail.couponCode})</span>}
                      value={<span className="text-green-400">− {fmtMoney(detail.discountAmount)}</span>}
                    />
                  )}
                  {!detail.couponCode && detail.discountAmount > 0 && (
                    <DetailRow
                      label="Discount"
                      value={<span className="text-red-400">− {fmtMoney(detail.discountAmount)}</span>}
                    />
                  )}
                  <DetailRow
                    label="Grand Total"
                    value={<span className="text-green-400 font-bold">{fmtMoney(detail.grandTotal)}</span>}
                  />
                  {detail.paidAmount > 0 && (
                    <DetailRow
                      label={detail.paymentStatus === 'Partial' ? 'Advance Paid' : 'Amount Paid'}
                      value={<span className="text-blue-400 font-semibold">{fmtMoney(detail.paidAmount)}</span>}
                    />
                  )}
                  {detail.grandTotal - detail.paidAmount > 0.01 && (
                    <DetailRow
                      label="Balance Due"
                      value={<span className="text-amber-400 font-bold">{fmtMoney(detail.grandTotal - detail.paidAmount)}</span>}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Guest Payment Submission */}
              {detail.manualPaymentRef && detail.paymentStatus === 'Pending' && (
                <Card className="bg-emerald-900/20 border-emerald-700/50">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-sm text-emerald-300 font-medium flex items-center gap-1.5">
                      <ShieldCheck className="h-4 w-4" /> Guest Payment Submission
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-3">
                    {detail.manualPaymentMethod && (
                      <DetailRow label="Method" value={
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                          {detail.manualPaymentMethod}
                        </span>
                      } />
                    )}
                    <DetailRow label="Transaction ID" value={
                      <span className="font-mono text-green-400 text-xs bg-green-900/30 px-2 py-0.5 rounded">{detail.manualPaymentRef}</span>
                    } />
                    {detail.manualPaymentAmount > 0 && (
                      <DetailRow label="Amount Sent" value={
                        <span className="text-emerald-300 font-bold">{fmtMoney(detail.manualPaymentAmount)}</span>
                      } />
                    )}
                    <DetailRow label="Status" value={
                      <span className="text-yellow-400 text-xs">Awaiting verification</span>
                    } />
                  </CardContent>
                </Card>
              )}

              {/* Lead Passenger */}
              {detail.leadPassenger && (
                <Card className="bg-slate-700/50 border-slate-600">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-sm text-slate-300 font-medium flex items-center gap-1.5">
                      <User className="h-4 w-4" /> Lead Guest
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-3">
                    <DetailRow label="Name" value={`${detail.leadPassenger.firstName} ${detail.leadPassenger.lastName}`} />
                    {detail.leadPassenger.email && (
                      <DetailRow
                        label={<span className="flex items-center gap-1"><Mail className="h-3 w-3" />Email</span>}
                        value={detail.leadPassenger.email}
                      />
                    )}
                    {detail.leadPassenger.phone && (
                      <DetailRow
                        label={<span className="flex items-center gap-1"><Phone className="h-3 w-3" />Phone</span>}
                        value={detail.leadPassenger.phone}
                      />
                    )}
                    {(detail.leadPassenger.address || detail.leadPassenger.city) && (
                      <DetailRow
                        label={<span className="flex items-center gap-1"><MapPin className="h-3 w-3" />Address</span>}
                        value={[detail.leadPassenger.address, detail.leadPassenger.city].filter(Boolean).join(', ')}
                      />
                    )}
                    {detail.leadPassenger.documentType && (
                      <DetailRow
                        label={detail.leadPassenger.documentType}
                        value={detail.leadPassenger.documentNumber}
                      />
                    )}
                  </CardContent>
                </Card>
              )}
              {/* Staff Activity */}
              <Card className="bg-slate-700/50 border-slate-600">
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm text-slate-300 font-medium">Activity Log</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3 space-y-1.5">
                  {[
                    {
                      label: 'Booked by',
                      name: detail.bookedByName || (
                        detail.source !== 'WalkIn'
                          ? (detail.leadPassenger
                              ? `${detail.leadPassenger.firstName} ${detail.leadPassenger.lastName}`.trim()
                              : 'Guest (Online)')
                          : null
                      ),
                    },
                    { label: 'Confirmed by',   name: detail.confirmedByName },
                    { label: 'Checked in by',  name: detail.checkedInByName },
                    { label: 'Checked out by', name: detail.checkedOutByName },
                  ].filter(e => e.name).map(e => (
                    <div key={e.label} className="flex justify-between py-0.5 text-sm">
                      <span className="text-slate-400">{e.label}</span>
                      <span className="text-white font-medium">{e.name}</span>
                    </div>
                  ))}

                  {/* Payment History Timeline */}
                  {detail.paymentHistory?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-700 space-y-2">
                      <p className="text-slate-500 text-xs font-semibold uppercase tracking-wide">Payment History</p>
                      {detail.paymentHistory.map((h, i) => (
                        <div key={i} className={`rounded-lg px-3 py-2 text-xs space-y-0.5 ${h.action === 'Confirmed' ? 'bg-green-900/20 border border-green-700/30' : 'bg-red-900/20 border border-red-700/30'}`}>
                          <div className="flex items-center justify-between">
                            <span className={`font-semibold ${h.action === 'Confirmed' ? 'text-green-400' : 'text-red-400'}`}>
                              {h.action === 'Confirmed' ? '✓ Payment Confirmed' : '✗ Payment Declined'}
                            </span>
                            {h.amountReceived != null && (
                              <span className="text-green-300 font-bold">৳{Number(h.amountReceived).toLocaleString()}</span>
                            )}
                          </div>
                          {h.byName && <div className="text-slate-400">By: <span className="text-slate-200">{h.byName}</span></div>}
                          {h.reason  && <div className="text-red-300">Reason: {h.reason}</div>}
                          <div className="text-slate-600">{new Date(h.at).toLocaleString('en-BD', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true })}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ── Verify Payment Dialog (guest submitted advance) ── */}
      {verifyTarget && (
        <Dialog open={true} onOpenChange={() => setVerifyTarget(null)}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-400" />
                Verify Payment — <span className="font-mono text-blue-400 text-sm">{verifyTarget.pnr}</span>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              {/* Guest submission details */}
              <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-xl p-3 space-y-2 text-sm">
                <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wide">Guest Submitted</p>
                {verifyTarget.method && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Method</span>
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      {verifyTarget.method}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Transaction ID</span>
                  <span className="font-mono text-green-400 text-xs bg-green-900/30 px-2 py-0.5 rounded border border-green-700/40">
                    {verifyTarget.txnId}
                  </span>
                </div>
                {verifyTarget.submittedAmount > 0 && (
                  <div className="flex justify-between items-center border-t border-emerald-700/30 pt-2">
                    <span className="text-slate-400">Amount Sent</span>
                    <span className="text-emerald-300 font-bold text-base">{fmtMoney(verifyTarget.submittedAmount)}</span>
                  </div>
                )}
              </div>

              {/* Pricing */}
              <div className="bg-slate-700/50 rounded-xl overflow-hidden text-sm">
                <div className="flex justify-between px-3 py-2 border-b border-slate-600/50">
                  <span className="text-slate-400">Grand Total</span>
                  <span className="text-white font-bold">{fmtMoney(verifyTarget.grandTotal)}</span>
                </div>
                {(() => {
                  const amt = parseFloat(verifyAmount);
                  if (isNaN(amt) || amt <= 0) return null;
                  const due = verifyTarget.grandTotal - amt;
                  return (
                    <>
                      <div className="flex justify-between px-3 py-2 border-b border-slate-600/50">
                        <span className="text-slate-400">Advance</span>
                        <span className="text-green-400 font-semibold">{fmtMoney(amt)}</span>
                      </div>
                      {due > 0.01 && (
                        <div className="flex justify-between px-3 py-2">
                          <span className="text-slate-400">Due at Check-in</span>
                          <span className="text-amber-400 font-semibold">{fmtMoney(due)}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Editable amount (in case admin needs to correct) */}
              <div className="space-y-1.5">
                <label className="text-sm text-slate-300 font-medium">Amount to Confirm (৳)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={verifyAmount}
                  onChange={e => setVerifyAmount(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-lg font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-xs text-slate-500">Pre-filled from guest submission. Adjust only if needed.</p>
              </div>

              {/* Verify checkbox */}
              <label className="flex items-start gap-3 cursor-pointer select-none bg-slate-700/40 rounded-lg p-3">
                <input
                  type="checkbox"
                  checked={verifyChecked}
                  onChange={e => setVerifyChecked(e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-emerald-500 flex-shrink-0"
                />
                <span className="text-sm text-slate-300 leading-relaxed">
                  I verified{verifyTarget.method ? <strong className="text-white"> {verifyTarget.method}</strong> : ''} txn{' '}
                  <strong className="font-mono text-green-400">{verifyTarget.txnId}</strong> is valid
                </span>
              </label>

              <Button
                onClick={handleVerifyConfirm}
                disabled={verifyLoading || !verifyAmount || !verifyChecked}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {verifyLoading ? 'Processing...' : `Confirm ${fmtMoney(parseFloat(verifyAmount) || 0)} Advance`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Manual Confirm Payment Dialog (walk-in / no guest submission) ── */}
      {detail && (
        <Dialog open={manualPayOpen} onOpenChange={setManualPayOpen}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Banknote className="h-5 w-5 text-amber-400" />
                Confirm Payment — <span className="font-mono text-blue-400 text-sm">{detail.pnr}</span>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              <div className="bg-slate-700/50 rounded-lg p-3 flex justify-between text-sm">
                <span className="text-slate-400">Grand Total</span>
                <span className="text-white font-bold">{fmtMoney(detail.grandTotal)}</span>
              </div>

              {/* Amount received */}
              <div className="space-y-1.5">
                <label className="text-sm text-slate-300 font-medium">Amount Received (৳)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={manualPayAmt}
                  onChange={e => setManualPayAmt(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-lg font-mono focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Optional Discount */}
              <div className="space-y-2">
                <label className="text-sm text-slate-400 font-medium">Apply Discount <span className="text-slate-500 font-normal">— optional</span></label>
                <div className="flex gap-2">
                  {[{ v: 0, label: 'Percentage (%)' }, { v: 1, label: 'Flat Amount (৳)' }].map(o => (
                    <button key={o.v} onClick={() => { setManualDiscType(o.v); setManualDiscValue(''); }}
                      className={`flex-1 py-1.5 rounded text-xs font-medium border transition-all ${
                        manualDiscType === o.v ? 'bg-red-600/60 border-red-500 text-white' : 'border-slate-600 text-slate-400'
                      }`}>{o.label}</button>
                  ))}
                </div>
                <input
                  type="number" min="0" step="0.01"
                  max={manualDiscType === 0 ? 100 : undefined}
                  value={manualDiscValue}
                  onChange={e => setManualDiscValue(e.target.value)}
                  placeholder={manualDiscType === 0 ? 'e.g. 10 (for 10%)' : 'e.g. 500'}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white font-mono focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              {/* Live preview */}
              {(() => {
                const discVal = parseFloat(manualDiscValue);
                const amt     = parseFloat(manualPayAmt);
                const hasDisc = !isNaN(discVal) && discVal > 0;
                if (hasDisc) {
                  const discAmt = manualDiscType === 0
                    ? Math.round(detail.grandTotal * discVal / 100 * 100) / 100
                    : Math.min(detail.grandTotal, discVal);
                  const newTotal = detail.grandTotal - discAmt;
                  return (
                    <div className="bg-slate-700/50 rounded-lg p-3 space-y-2 text-sm">
                      <div className="flex justify-between text-red-400">
                        <span>Discount ({manualDiscType === 0 ? `${discVal}%` : `৳${discVal}`})</span>
                        <span>− {fmtMoney(discAmt)}</span>
                      </div>
                      <div className="flex justify-between text-green-400 font-bold border-t border-slate-600 pt-2">
                        <span>New Total (Fully Paid)</span><span>{fmtMoney(newTotal)}</span>
                      </div>
                    </div>
                  );
                }
                if (!isNaN(amt) && amt > 0) {
                  const due = detail.grandTotal - amt;
                  const isFull = due < 0.01;
                  return (
                    <div className="bg-slate-700/50 rounded-lg p-3 space-y-2 text-sm">
                      <div className="flex justify-between text-green-400 font-bold">
                        <span>{isFull ? 'Full Payment' : 'Advance'}</span><span>{fmtMoney(amt)}</span>
                      </div>
                      {!isFull && (
                        <div className="flex justify-between text-amber-400 border-t border-slate-600 pt-2">
                          <span>Due at Check-in</span><span>{fmtMoney(due)}</span>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}

              <Button
                onClick={handleManualPayConfirm}
                disabled={manualPayLoading || (!manualPayAmt && !manualDiscValue)}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                {manualPayLoading ? 'Processing...' : 'Confirm Payment'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Decline Payment Dialog ── */}
      {declineTarget && (
        <Dialog open={true} onOpenChange={() => setDeclineTarget(null)}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ShieldX className="h-5 w-5 text-red-400" />
                Decline Payment — <span className="font-mono text-blue-400 text-sm">{declineTarget.pnr}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-1">
              <p className="text-slate-400 text-sm">
                This will clear the guest's submitted payment reference and reset the payment status to Pending. The guest will be able to re-submit.
              </p>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-300">Reason for declining <span className="text-red-400">*</span></label>
                <textarea
                  rows={3}
                  value={declineReason}
                  onChange={e => setDeclineReason(e.target.value)}
                  placeholder="e.g. Invalid transaction ID, amount mismatch..."
                  className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-red-500 resize-none"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700" onClick={() => setDeclineTarget(null)}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-700 hover:bg-red-800 text-white"
                  disabled={declineLoading || !declineReason.trim()}
                  onClick={handleDeclineConfirm}
                >
                  {declineLoading ? 'Processing...' : 'Decline Payment'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
